"use client";

import {
  FileTree,
  FileTreeFile,
  FileTreeFolder,
} from "@/components/ai-elements/file-tree";
import { Suggestion } from "@/components/ai-elements/suggestion";
import { AudioLinesIcon } from "@/components/ui/audio-lines";
import { BotMessageSquareIcon } from "@/components/ui/bot-message-square";
import { BrainIcon } from "@/components/ui/brain";
import { FeatherIcon } from "@/components/ui/feather";
import { FoldersIcon } from "@/components/ui/folders";
import { GalleryVerticalEndIcon } from "@/components/ui/gallery-vertical-end";
import { MessageSquareDashedIcon } from "@/components/ui/message-square-dashed";
import { LayoutPanelTopIcon } from "@/components/ui/layout-panel-top";
import { PlusIcon } from "@/components/ui/plus";
import { UsersIcon } from "@/components/ui/users";
import { UploadIcon } from "@/components/ui/upload";
import { BookOpenTextIcon } from "lucide-react";
import { MessageCircleIcon } from "@/components/ui/message-circle";
import { DocsHelpChat } from "@/components/docs-help-chat";
import { PageHeader } from "@/components/page-header";
import { SquarePenIcon } from "@/components/ui/square-pen";
import { SendIcon } from "@/components/ui/send";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import {
  DOC_SECTIONS,
  searchDocs,
  type DocItem,
  type DocSection,
  type DocSnippetMessage,
} from "@/lib/docs";
import type { ChatUIMessage } from "@/types/chat";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { BotIcon, CheckCircle2Icon, GripVerticalIcon, LockIcon, MicIcon, MicOffIcon, SearchIcon, Square, SquareIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { CopyIcon } from "@/components/ui/copy";
import { DownloadIcon } from "@/components/ui/download";
import { PlayIcon } from "@/components/ui/play";
import { SparklesIcon } from "@/components/ui/sparkles";
import { ThinkingIndicator } from "@/components/thinking-indicator";
import { Textarea } from "@/components/ui/textarea";
import { RefreshCwIcon, XIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  PromptInput,
  PromptInputBody,
  PromptInputFooter,
  PromptInputProvider,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputController,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { nanoid } from "nanoid";
import { Highlighter } from "@/components/ui/highlighter";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageResponse } from "@/components/ai-elements/message";
import { Button } from "@/components/ui/button";


const BADGE_CLASSES: Record<string, string> = {
  Admin: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  Developer: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

function toUIMessages(seed: DocSnippetMessage[]): ChatUIMessage[] {
  return seed.map((m) => ({
    id: nanoid(),
    role: m.role,
    parts: [{ type: "text" as const, text: m.text }],
    content: m.text,
  }));
}

const DOCS_TRANSPORT = new DefaultChatTransport({
  api: "/api/chat",
  body: () => ({
    model: "claude-haiku-4-5",
    webSearch: false,
    webFetch: false,
    memory: false,
    codeExecution: false,
    incognito: true,
    source: "docs-preview",
  }),
});

const AGENT_DEMO_TRANSPORT = new DefaultChatTransport({
  api: "/api/chat",
  body: () => ({
    model: "claude-haiku-4-5",
    webSearch: false,
    webFetch: false,
    memory: false,
    codeExecution: false,
    incognito: true,
    source: "docs-agent-demo",
    agentPrompt:
      "You are a senior civil engineer at a New Zealand consultancy. Give concise, practical advice. Reference NZ standards (NZS 3604, AS/NZS standards) where relevant. Keep responses focused and under 200 words.",
  }),
});

const AGENT_DEMO_SEED: DocSnippetMessage[] = [
  {
    role: "user",
    text: "What foundation type would you recommend for a 3-storey office on moderately expansive clay?",
  },
  {
    role: "assistant",
    text: "For a 3-storey office on moderately expansive clay, I'd recommend **bored pier foundations** extending below the active zone (typically 1.5–3 m in NZ), paired with a suspended ground-floor slab.\n\nKey reasons:\n- Piers bypass seasonal moisture changes in the upper clay layer\n- A suspended slab avoids direct contact with swelling soil\n- Consistent bearing depth reduces differential movement risk\n\nYou'll want a geotechnical investigation to confirm the active zone depth and clay plasticity index before finalising the design.",
  },
];

const DOC_PAGE_ICONS: Record<string, React.ElementType> = {
  "agents":             BotMessageSquareIcon,
  "knowledge-base":     FoldersIcon,
  "meetings":           AudioLinesIcon,
  "prompt-library":     MessageSquareDashedIcon,
  "memories":           BrainIcon,
  "cv-upload":          UploadIcon,
  "files-uploads":      UploadIcon,
  "files-creating":     SquarePenIcon,
  "writing-style-profile": FeatherIcon,
  "projects":           GalleryVerticalEndIcon,
  "project-context":    GalleryVerticalEndIcon,
  "project-agents-widgets": BotMessageSquareIcon,
  "project-members":    UsersIcon,
  "teams":              UsersIcon,
};

const WRITING_STYLE_PRESETS = [
  { id: "personal", label: "My saved style" },
  { id: "paragraph-polished", label: "Paragraph - polished engineering note" },
  { id: "bullet-points", label: "Bullet points - actions and checks" },
  { id: "headings-report", label: "Headings - mini report format" },
  { id: "technical-precise", label: "Technical style - precise design note" },
  { id: "funny-field-note", label: "Funny - light field note" },
] as const;

type WritingStylePresetId = (typeof WRITING_STYLE_PRESETS)[number]["id"];

const WRITING_STYLE_SAMPLE_TEXT =
  "The proposed stormwater upgrade for the eastern catchment should prioritise attenuation before discharge to the downstream network. Recent site observations indicate shallow overland flow during high-intensity rainfall, particularly near the low point beside the access road. We recommend confirming inlet capacity, checking the downstream pipe gradient, and allowing for a maintenance access strip along the swale so the asset remains serviceable after handover.";

type DocsPanelProps = {
  onBack: () => void;
  initialDocId?: string | null;
  userRole?: string | null;
};

type SearchResult = { section: DocSection; item: DocItem };

const canViewItem = (
  item: DocItem,
  userRole?: string | null
): boolean => {
  // No badge = everyone can view
  if (!item.badge) return true;
  // Admin badge = only admins and superadmins
  if (item.badge === "Admin") {
    return userRole === "admin" || userRole === "superadmin";
  }
  // Developer badge = everyone can view
  return true;
};

const canViewSection = (
  section: DocSection,
  userRole?: string | null
): boolean => {
  // A section is viewable if it has at least one viewable item
  return section.items.some((item) => canViewItem(item, userRole));
};

export function DocumentationPanel({ onBack, initialDocId, userRole }: DocsPanelProps) {
  const [search, setSearch] = useState("");
  
  // Filter sections and items based on user role
  const visibleSections = useMemo(
    () =>
      DOC_SECTIONS.filter((section) => canViewSection(section, userRole)).map(
        (section) => ({
          ...section,
          items: section.items.filter((item) => canViewItem(item, userRole)),
        })
      ),
    [userRole]
  );

  const [activeDocId, setActiveDocId] = useState<string>(
    initialDocId ?? visibleSections[0]?.items[0]?.id ?? ""
  );
  // Ask (chat) is the default; deep links to a page open the guides instead.
  const [view, setView] = useState<"ask" | "browse">(initialDocId ? "browse" : "ask");
  const [prevInitialDocId, setPrevInitialDocId] = useState(initialDocId);
  if (initialDocId !== prevInitialDocId) {
    setPrevInitialDocId(initialDocId);
    if (initialDocId) {
      setActiveDocId(initialDocId);
      setView("browse");
    }
  }

  const handleOpenDoc = useCallback((docId: string) => {
    setActiveDocId(docId);
    setView("browse");
  }, []);

  const activeDoc = useMemo(() => {
    for (const section of visibleSections) {
      const found = section.items.find((item) => item.id === activeDocId);
      if (found) return found;
    }
    return null;
  }, [activeDocId, visibleSections]);

  const searchResults: SearchResult[] = useMemo(
    () =>
      search.trim()
        ? searchDocs(search).filter(
            ({ item }) => canViewItem(item, userRole)
          )
        : [],
    [search, userRole]
  );

  const isSearching = search.trim().length > 0;

  const [navWidth, setNavWidth] = useState(224);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const handleDividerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - rect.left;
      setNavWidth(Math.max(160, Math.min(400, newWidth)));
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <PageHeader
        title="Documentation"
        description={view === "ask" ? "Ask anything about Woods AI" : "Guides and reference for Woods AI"}
        onBack={onBack}
        actions={
          view === "ask" ? (
            <Button className="gap-1.5" onClick={() => setView("browse")} size="sm" type="button" variant="outline">
              <BookOpenTextIcon className="size-3.5" />
              Browse docs
            </Button>
          ) : (
            <Button className="gap-1.5" onClick={() => setView("ask")} size="sm" type="button" variant="outline">
              <MessageCircleIcon size={14} />
              Ask a question
            </Button>
          )
        }
      />

      {/* Ask — stays mounted while browsing so the conversation survives */}
      <div className={cn("min-h-0 flex-1 flex-col overflow-hidden", view === "ask" ? "flex" : "hidden")}>
        <DocsHelpChat onOpenDoc={handleOpenDoc} />
      </div>

      {/* Browse */}
      <div ref={containerRef} className={cn("min-h-0 flex-1 overflow-hidden", view === "browse" ? "flex" : "hidden")}>
        {/* Left nav */}
        <div className="flex min-h-0 shrink-0 flex-col overflow-hidden" style={{ width: navWidth }}>
          <div className="p-3 border-b border-border">
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8 h-8 text-sm"
                placeholder="Search docs…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <ScrollArea className="min-h-0 flex-1 overflow-hidden">
            <div className="p-2">
              {isSearching ? (
                searchResults.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground">
                    No results for &ldquo;{search}&rdquo;
                  </p>
                ) : (
                  <div>
                    <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Results
                    </div>
                    {searchResults.map(({ item }) => (
                      <NavItem
                        key={item.id}
                        item={item}
                        active={activeDocId === item.id}
                        onClick={() => {
                          setActiveDocId(item.id);
                          setSearch("");
                        }}
                      />
                    ))}
                  </div>
                )
              ) : (
                visibleSections.map((section) => (
                  <div key={section.id} className="mb-4">
                    <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {section.title}
                    </div>
                    {section.items.map((item) => (
                      <NavItem
                        key={item.id}
                        item={item}
                        active={activeDocId === item.id}
                        onClick={() => setActiveDocId(item.id)}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Drag handle */}
        <div
          className="relative w-px shrink-0 cursor-col-resize bg-border hover:bg-primary/40 transition-colors"
          onMouseDown={handleDividerMouseDown}
        >
          <div className="absolute inset-y-0 -left-1.5 -right-1.5" />
        </div>

        {/* Content */}
        <ScrollArea className="min-h-0 flex-1 overflow-hidden">
          <div className="mx-auto max-w-3xl px-10 py-10">
            {activeDoc ? (
              <div>
                {activeDoc.badge ? (
                  <span
                    className={cn(
                      "mb-4 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium",
                      BADGE_CLASSES[activeDoc.badge] ?? ""
                    )}
                  >
                    {activeDoc.badge} only
                  </span>
                ) : null}
                {activeDoc.id === "key-concepts" ? (
                  <KeyConceptsContent onNavigate={setActiveDocId} />
                ) : activeDoc.id === "quick-start" ? (
                  <QuickStartContent />
                ) : activeDoc.id === "first-chat" ? (
                  <FirstChatContent />
                ) : activeDoc.id === "agents" ? (
                  <AgentsContent onNavigate={setActiveDocId} />
                ) : activeDoc.id === "projects" ? (
                  <ProjectsContent onNavigate={setActiveDocId} />
                ) : activeDoc.id === "knowledge-base" ? (
                  <KnowledgeBaseContent onNavigate={setActiveDocId} />
                ) : activeDoc.id === "meetings" ? (
                  <MeetingsContent />
                ) : activeDoc.id === "prompt-library" ? (
                  <PromptLibraryContent />
                ) : activeDoc.id === "widgets-generator" ? (
                  <WidgetGeneratorContent
                    content={activeDoc.content}
                    pageIcon={DOC_PAGE_ICONS[activeDoc.id]}
                    pageTitle={activeDoc.title}
                  />
                ) : activeDoc.id === "project-agents-widgets" ? (
                  <ProjectAgentsWidgetsContent
                    content={activeDoc.content}
                    pageIcon={DOC_PAGE_ICONS[activeDoc.id]}
                    pageTitle={activeDoc.title}
                  />
                ) : activeDoc.id === "writing-style-profile" ? (
                  <WritingStyleProfileContent
                    content={activeDoc.content}
                    pageIcon={DOC_PAGE_ICONS[activeDoc.id]}
                    pageTitle={activeDoc.title}
                  />
                ) : (
                  <DocContent
                    content={activeDoc.content}
                    pageIcon={DOC_PAGE_ICONS[activeDoc.id]}
                    pageTitle={activeDoc.title}
                  />
                )}
                {activeDoc.snippet ? (
                  <ChatSnippet seed={activeDoc.snippet} />
                ) : null}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                Select a topic from the left.
              </p>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

function WidgetGeneratorContent({
  content,
  pageIcon,
  pageTitle,
}: {
  content: string;
  pageIcon?: React.ElementType;
  pageTitle?: string;
}) {
  return (
    <div>
      <DocContent content={content} pageIcon={pageIcon} pageTitle={pageTitle} />
      <WidgetGeneratorPlayground />
    </div>
  );
}

type WidgetSuggestion = {
  category: string;
  label: string;
  prompt: string;
};

const WIDGET_SUGGESTIONS: WidgetSuggestion[] = [
  // Civil Engineering
  { category: "Civil Engineering", label: "Stormwater Pipe Sizer", prompt: "A stormwater pipe sizing calculator using Manning's equation for NZ design. Inputs: pipe diameter (NZ standard sizes 150, 225, 300, 375, 450, 525, 600 mm), slope (%), Manning's n (default 0.013 for concrete). Calculate flow capacity (L/s and m³/s) and velocity (m/s). Highlight velocity outside 0.6–3.0 m/s in red. Show a summary table across all standard diameters at the chosen slope." },
  { category: "Civil Engineering", label: "Retaining Wall Stability", prompt: "A retaining wall stability checker for NZ geotechnical practice. Inputs: wall height (m), retained soil unit weight (kN/m³, default 18), friction angle (degrees, default 30), surcharge (kPa), base width (m), base depth (m). Calculate FOS against overturning, sliding, and bearing capacity. Show each as a pass/fail badge (FOS required: 1.5 overturning, 1.5 sliding, 3.0 bearing). Include a simple SVG cross-section diagram." },
  // Water Infrastructure
  { category: "Water Infrastructure", label: "Hazen-Williams Head Loss", prompt: "A watermain head loss calculator using the Hazen-Williams formula for NZ water supply design. Inputs: pipe diameter (DN50, 80, 100, 150, 200, 250, 300 mm), flow rate (L/s), pipe material with auto-filled C coefficient (uPVC 150, Ductile Iron 130, Steel 120, HDPE 140), pipe length (m), inlet pressure (m). Calculate head loss (m), velocity (m/s), and residual pressure. Flag velocity outside 0.5–2.0 m/s and pressure below 15 m (NZS 4404 reference)." },
  { category: "Water Infrastructure", label: "Rational Method Q", prompt: "A stormwater peak flow calculator using the Rational Method (Q = CiA / 360) for NZ catchments. Inputs: catchment area (ha), runoff coefficient C (with a land use guide table), NZ rainfall region selector (Auckland, Hamilton, Tauranga, Wellington, Christchurch, Dunedin — pre-filled with typical 10-year 1-hour rainfall intensities in mm/hr), return period (2, 5, 10, 50, 100 year). Calculate Q in m³/s and L/s. Recommend a standard NZ pipe diameter. Show a sensitivity table varying area and C." },
  // Surveying
  { category: "Surveying", label: "Traverse Closure", prompt: "A survey traverse closure calculator for NZ cadastral and engineering surveys. Input up to 8 legs, each with whole-circle bearing (degrees, minutes, seconds) and horizontal distance (m). Calculate: angular misclosure, linear misclosure (ΔE, ΔN), traverse perimeter, precision ratio (1:N). Apply Bowditch (Compass Rule) correction and display adjusted coordinates. Draw a scaled SVG traverse diagram with North arrow and labelled stations." },
  { category: "Surveying", label: "Area by Coordinates", prompt: "A cadastral area calculator using the Gauss Shoelace (cross-product) method. Input up to 20 traverse points as Easting/Northing pairs (NZTM2000). Calculate area in m² and hectares, and perimeter in metres. Display a live SVG polygon as points are entered, scaled to fit. Show the coordinate table with a running area calculation. Support both clockwise and anti-clockwise entry." },
  // Geospatial
  { category: "Geospatial", label: "NZTM2000 Converter", prompt: "A New Zealand coordinate converter between NZTM2000 (Easting/Northing) and WGS84 (Decimal Degrees and DMS). Include preset buttons for Auckland CBD, Wellington CBD, Christchurch CBD, Queenstown, Hamilton, and Tauranga. Show both coordinate systems live as the user types. Include a copy-to-clipboard button for each format." },
  { category: "Geospatial", label: "NZ Region Map", prompt: "An interactive SVG map of New Zealand's 16 regional councils with simplified, recognisable outlines. On hover, show a tooltip with region name, land area (km²), and approximate population. Colour regions using a green-to-blue scale by area. Include a North Island / South Island toggle. Label major cities (Auckland, Wellington, Christchurch, Dunedin, Hamilton, Tauranga)." },
  // Earthworks
  { category: "Earthworks", label: "Cut/Fill End Area Volumes", prompt: "An earthworks volume calculator using both the End Area and Prismoidal methods. Enter up to 12 cross-sections with chainage (m) and separate cut area and fill area (m²) for each. Calculate cumulative cut volume, fill volume, and net volume (m³) with a bulking/shrinkage factor input (default 1.25 cut, 0.9 fill). Show a Chart.js mass haul diagram: cut/fill bar chart with cumulative net volume line overlay. Include a summary table." },
  { category: "Earthworks", label: "Batter Slope Setback", prompt: "A batter slope setback calculator for NZ earthworks design. Inputs: cut or fill (toggle), height (m), batter slope (H:V, choose from 1:1, 1.5:1, 2:1, 3:1, 4:1), bench width (m, for batters over 4 m), top-of-cut or toe-of-fill offset (m). Calculate horizontal setback required. Show a clean SVG cross-section diagram with labels. Include a table comparing setbacks for all standard slopes at the entered height." },
  // Planning & Urban Design
  { category: "Planning & Urban Design", label: "Subdivision Lot Yield", prompt: "A subdivision lot yield estimator for NZ land development. Inputs: gross site area (ha), zone type (selector: General Residential, Medium Density, Mixed Use, Rural Residential), average road reserve width (m), estimated infrastructure footprint %. Calculate net developable area, estimated lot yield at user-defined average lot size (m²), and indicative development cost per lot using a cost/m² input. Show results as a summary card and a donut chart of area allocation (roads, infrastructure, lots)." },
  { category: "Planning & Urban Design", label: "Site Coverage Calculator", prompt: "A NZ site coverage and floor area ratio (FAR) calculator for resource consent applications. Inputs: site area (m²), proposed building footprint (m²), number of storeys, impervious area (m²). Calculate site coverage %, FAR (GFA/site area), and impervious surface %. Compare against common Auckland Unitary Plan zone thresholds (Residential — Terrace Housing and Apartment Buildings: 60% coverage, GRZ: 40%). Show a simple stacked bar comparing proposed vs permitted." },
  // Land Development
  { category: "Land Development", label: "Infrastructure Cost Split", prompt: "An infrastructure cost apportionment calculator for NZ land development. Input total infrastructure cost (NZD) and up to 8 lots with their area (m²) and a weighting factor (1 = standard, adjust for corner lots, etc.). Apportion costs by lot area and by equal share. Show both methods side by side in a table. Calculate each lot's share in dollars and as a percentage. Include a bar chart comparing the two methods." },
  { category: "Land Development", label: "Flood Freeboard Check", prompt: "A flood freeboard and floor level checker for NZ land development under NZS 4404. Inputs: 1% AEP flood level (m, enter or estimate), freeboard requirement (default 500 mm residential, 300 mm non-residential), proposed finished floor level (m), site ground level (m). Calculate minimum floor level required, actual freeboard provided, and pass/fail status. Show a clear elevation diagram with labelled datums and a compliance summary table." },
];

const WIDGET_CATEGORIES = [...new Set(WIDGET_SUGGESTIONS.map((s) => s.category))];

function WidgetGeneratorPlayground() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const visible = activeCategory
    ? WIDGET_SUGGESTIONS.filter((s) => s.category === activeCategory)
    : WIDGET_SUGGESTIONS;

  const handleGenerate = useCallback(async (promptText: string) => {
    if (!promptText.trim()) return;
    setIsGenerating(true);
    setGeneratedHtml(null);
    try {
      const res = await fetch("/api/docs/generate-widget", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: promptText.trim() }),
      });
      if (!res.ok) throw new Error("Generation failed");
      const { html } = (await res.json()) as { html: string };
      setGeneratedHtml(html);
    } catch {
      setGeneratedHtml(null);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return (
    <div className="mt-8 flex w-full flex-col gap-4 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-medium text-foreground">Try the generator</h2>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Click a suggestion to load its prompt, then generate a live interactive HTML page.
        </p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setActiveCategory(null)}
          className={cn(
            "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
            activeCategory === null
              ? "border-foreground bg-foreground text-background"
              : "border-border bg-background text-muted-foreground hover:border-foreground hover:text-foreground"
          )}
        >
          All
        </button>
        {WIDGET_CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
              activeCategory === cat
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-background text-muted-foreground hover:border-foreground hover:text-foreground"
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Suggestion pills */}
      <div className="flex flex-wrap gap-2">
        {visible.map((s) => (
          <Suggestion
            key={s.label}
            suggestion={s.label}
            className={cn(prompt === s.prompt && "bg-accent text-accent-foreground")}
            onClick={() => setPrompt(s.prompt)}
          >
            {s.label}
          </Suggestion>
        ))}
      </div>

      {/* Prompt textarea */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-foreground">Prompt</p>
          {prompt && (
            <button
              onClick={() => setPrompt("")}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <XIcon className="size-3" />
              Clear
            </button>
          )}
        </div>
        <Textarea
          className="min-h-24 resize-y font-mono text-xs"
          placeholder="Select a suggestion above or describe your own tool…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              void handleGenerate(prompt);
            }
          }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">⌘ Enter to generate</span>
        <Button
          disabled={!prompt.trim() || isGenerating}
          onClick={() => void handleGenerate(prompt)}
          size="sm"
        >
          {isGenerating ? (
            <>
              <SparklesIcon className="mr-2 size-3.5" />
              Generating…
            </>
          ) : (
            <>
              <SparklesIcon className="mr-2 size-3.5" />
              Generate
            </>
          )}
        </Button>
      </div>

      {/* Inline preview */}
      {(isGenerating || generatedHtml) && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-foreground">Preview</p>
            {generatedHtml && !isGenerating && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => void handleGenerate(prompt)}
                disabled={isGenerating}
              >
                <RefreshCwIcon className="mr-1.5 size-3.5" />
                Regenerate
              </Button>
            )}
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            {isGenerating ? (
              <div className="flex h-48 items-center justify-center">
                <ThinkingIndicator />
              </div>
            ) : (
              <iframe
                className="h-150 w-full"
                sandbox="allow-scripts allow-same-origin"
                srcDoc={generatedHtml ?? ""}
                title="Widget preview"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WritingStyleProfileContent({
  content,
  pageIcon,
  pageTitle,
}: {
  content: string;
  pageIcon?: React.ElementType;
  pageTitle?: string;
}) {
  return (
    <div>
      <DocContent content={content} pageIcon={pageIcon} pageTitle={pageTitle} />
      <WritingStyleRenditionPlayground />
    </div>
  );
}

function WritingStyleRenditionPlayground() {
  const sourceText = WRITING_STYLE_SAMPLE_TEXT;
  const [selectedStyleId, setSelectedStyleId] =
    useState<WritingStylePresetId>("personal");
  const [rendition, setRendition] = useState("");
  const [visibleRendition, setVisibleRendition] = useState("");
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleRender = useCallback(async () => {
    const trimmed = sourceText.trim();
    if (!trimmed || isRendering) return;

    setIsRendering(true);
    setRenderError("");
    setCopied(false);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const response = await fetch("/api/writing-style/rendition", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
        body: JSON.stringify({
          styleId: selectedStyleId,
          text: trimmed,
        }),
      });

      const data = (await response.json().catch(() => null)) as {
        error?: string;
        rendition?: string;
      } | null;

      if (!response.ok) {
        throw new Error(data?.error ?? "Could not rewrite this text.");
      }

      setRendition(data?.rendition?.trim() ?? "");
    } catch (err) {
      setRenderError(
        err instanceof Error ? err.message : "Could not rewrite this text."
      );
    } finally {
      setIsRendering(false);
    }
  }, [isRendering, selectedStyleId, sourceText]);

  const handleCopy = useCallback(async () => {
    if (!rendition) return;
    await navigator.clipboard.writeText(rendition);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }, [rendition]);

  useEffect(() => {
    if (!rendition) {
      setVisibleRendition("");
      return;
    }

    setVisibleRendition("");
    let index = 0;
    const chunkSize = 8;
    const interval = window.setInterval(() => {
      index = Math.min(index + chunkSize, rendition.length);
      setVisibleRendition(rendition.slice(0, index));

      if (index >= rendition.length) {
        window.clearInterval(interval);
      }
    }, 18);

    return () => window.clearInterval(interval);
  }, [rendition]);

  return (
    <div className="mt-8 flex w-full flex-col gap-4 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-medium text-foreground">Try a rendition</h2>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Paste a piece of text, pick a style, and Woods AI will rewrite it while
          preserving the meaning.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {WRITING_STYLE_PRESETS.map((style) => (
          <Suggestion
            key={style.id}
            className={cn(
              selectedStyleId === style.id && "bg-accent text-accent-foreground"
            )}
            onClick={(suggestion) =>
              setSelectedStyleId(suggestion as WritingStylePresetId)
            }
            suggestion={style.id}
          >
            {style.label}
          </Suggestion>
        ))}
      </div>

      <div className="flex min-h-64 flex-col rounded-lg border border-border bg-background">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <p className="text-xs font-medium text-foreground">Conversation preview</p>
            <span className="text-[10px] text-muted-foreground">Streamdown</span>
          </div>

          <div className="flex flex-1 flex-col gap-3 overflow-y-auto bg-muted/30 px-3 py-3">
            <div className="flex justify-end gap-2">
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-secondary px-3 py-2 text-xs leading-relaxed text-foreground">
                <p className="mb-2 font-medium">
                  Rewrite this in{" "}
                  {
                    WRITING_STYLE_PRESETS.find(
                      (style) => style.id === selectedStyleId
                    )?.label
                  }
                  .
                </p>
                <p className="whitespace-pre-wrap">{sourceText}</p>
              </div>
              <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                You
              </div>
            </div>

            <div className="flex justify-start gap-2">
              <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground text-[10px] font-semibold text-background">
                W
              </div>
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-border bg-background px-3 py-2 text-sm leading-relaxed text-foreground">
                {rendition ? (
                  <MessageResponse
                    isAnimating={visibleRendition.length < rendition.length}
                    disallowedElements={["script", "style"]}
                  >
                    {visibleRendition}
                  </MessageResponse>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Your rewritten version will appear here after you click
                    Rewrite.
                  </p>
                )}
              </div>
            </div>
          </div>
      </div>

      {renderError ? (
        <p className="text-xs text-destructive">{renderError}</p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {sourceText.trim().length.toLocaleString()} characters
        </p>
        <div className="flex items-center gap-2">
          {rendition ? (
            <Button
              className="gap-2"
              onClick={handleCopy}
              size="sm"
              type="button"
              variant="outline"
            >
              <CopyIcon className="size-3.5" />
              {copied ? "Copied" : "Copy"}
            </Button>
          ) : null}
          <Button
            className="gap-2"
            disabled={!sourceText.trim() || isRendering}
            onClick={handleRender}
            size="sm"
            type="button"
          >
            <SendIcon className="size-3.5" />
            {isRendering ? "Rewriting..." : "Rewrite"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NavItem({
  item,
  active,
  onClick,
}: {
  item: DocItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
        active
          ? "bg-accent text-accent-foreground font-medium"
          : "text-foreground hover:bg-muted"
      )}
    >
      <span className="truncate">{item.title}</span>
      {item.badge ? (
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
            BADGE_CLASSES[item.badge] ?? ""
          )}
        >
          {item.badge}
        </span>
      ) : null}
    </button>
  );
}

function DocContent({
  content,
  pageIcon: Icon,
  pageTitle,
}: {
  content: string;
  pageIcon?: React.ElementType;
  pageTitle?: string;
}) {
  const body = Icon ? content.replace(/^#[^\n]+\n/, "") : content;
  return (
    <div>
      {Icon && pageTitle && (
        <h1 className="mt-6 mb-2 font-semibold text-3xl flex items-center gap-2.5">
          <Icon size={24} className="shrink-0 text-foreground" />
          {pageTitle}
        </h1>
      )}
      <MessageResponse
        isAnimating={false}
      >
        {body}
      </MessageResponse>
    </div>
  );
}

function KeyConceptsContent({ onNavigate }: { onNavigate: (docId: string) => void }) {
  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl">Key Concepts</h1>
      <p className="mt-2 text-sm">
        Woods AI is a collaborative AI platform. Before diving in, here are the six ideas that underpin everything else.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Teams</h2>
      <p className="mt-2 text-sm">
        A team is a shared environment for collaboration. Everything — chats, agents, files, meetings, prompts — lives inside a team. You can belong to multiple teams and switch between them from the sidebar.
      </p>
      <p className="mt-2 text-sm">
        Your <strong>Personal team</strong> is created automatically when you sign up. It&apos;s{" "}
        <Highlighter action="circle" color="#94a3b8" isView animationDuration={700}>
          private to you by default
        </Highlighter>
        .
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Agents</h2>
      <p className="mt-2 text-sm">
        An agent is a pre-configured AI assistant with a defined purpose. Instead of re-explaining your context in every chat, you create an agent once with a system prompt, a chosen model, and the tools it needs — then reuse it across conversations.
      </p>
      <p className="mt-2 text-sm">
        Think of agents as{" "}
        <Highlighter action="highlight" color="#fde68a" darkColor="#92400e" isView animationDuration={800}>
          saved AI personas
        </Highlighter>
        : a &ldquo;Research Assistant&rdquo;, a &ldquo;Meeting Summariser&rdquo;, or a &ldquo;Civil Engineering Advisor&rdquo;.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Tools</h2>
      <p className="mt-2 text-sm">Tools extend what the AI can do mid-conversation:</p>
      <div className="my-4 w-full overflow-hidden rounded-xl border border-border">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">Tool</th>
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">What it does</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr><td className="px-4 py-2 text-sm">Web Search</td><td className="px-4 py-2 text-sm">Searches the web in real time</td></tr>
            <tr><td className="px-4 py-2 text-sm">Web Fetch</td><td className="px-4 py-2 text-sm">Reads the content of a specific URL</td></tr>
            <tr><td className="px-4 py-2 text-sm">Code Execution</td><td className="px-4 py-2 text-sm">Runs Python and processes files (PDF, DOCX, XLSX, PPTX)</td></tr>
            <tr><td className="px-4 py-2 text-sm">Memory</td><td className="px-4 py-2 text-sm">Recalls facts you&apos;ve stored about yourself</td></tr>
            <tr><td className="px-4 py-2 text-sm">Custom Tools</td><td className="px-4 py-2 text-sm">Webhooks, internal APIs, and AWS tasks you configure</td></tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-sm">
        Tools can be{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          enabled per-agent or toggled per-session
        </Highlighter>
        .
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Memory</h2>
      <p className="mt-2 text-sm">
        Memory lets the AI remember things across conversations — your role, preferences, project context.{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          You control what&apos;s stored
        </Highlighter>
        , and you can delete memories at any time from the Memories panel.
      </p>
      <p className="mt-2 text-sm">
        Incognito mode disables memory entirely for a session.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Personal profile</h2>
      <p className="mt-2 text-sm">
        Your profile gives Woods AI optional background about you. Your CV helps it understand your role, experience, skills, education, and achievements. Your writing style profile helps it draft and edit in your own voice when that tool is enabled.
      </p>
      <div className="my-4 w-full overflow-hidden rounded-xl border border-border">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">Profile item</th>
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">What it helps with</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr><td className="px-4 py-2 text-sm">CV / Resume</td><td className="px-4 py-2 text-sm">Personalises answers using your professional background</td></tr>
            <tr><td className="px-4 py-2 text-sm">Writing Style Profile</td><td className="px-4 py-2 text-sm">Guides writing, rewriting, and editing in your voice</td></tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-sm">
        You can upload or update both from <strong>Profile → General</strong>. Learn more:{" "}
        <button
          className="font-medium text-primary underline underline-offset-2"
          onClick={() => onNavigate("cv-upload")}
          type="button"
        >
          CV / Resume Upload
        </button>
        {" · "}
        <button
          className="font-medium text-primary underline underline-offset-2"
          onClick={() => onNavigate("writing-style-profile")}
          type="button"
        >
          Writing Style Profile
        </button>
        .
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Layouts</h2>
      <p className="mt-2 text-sm">
        Layouts are markdown templates that define the structure of a document — most commonly used for meeting minutes. Create a layout once and share it with your team; it&apos;s then available whenever minutes are generated from a meeting recording.
      </p>

      <hr className="my-6 border-border" />
      <p className="mt-2 text-sm">
        <strong>Next steps:</strong>{" "}
        <span className="font-medium text-primary underline cursor-pointer">Quick Start</span>
        {" → "}
        <span className="font-medium text-primary underline cursor-pointer">Your First Chat</span>
      </p>
    </div>
  );
}

function QuickStartContent() {
  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl">Quick Start</h1>
      <p className="mt-2 text-sm">
        Get from sign-in to a useful result in{" "}
        <Highlighter action="circle" color="#94a3b8" isView animationDuration={700}>
          under five minutes
        </Highlighter>
        .
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">1. Sign in</h2>
      <p className="mt-2 text-sm">
        Open Woods AI and sign in with your organisational account. You&apos;ll land on the chat screen inside your Personal team.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">2. Start a chat</h2>
      <p className="mt-2 text-sm">
        Type your question or request in the input at the bottom of the screen and press <strong>Enter</strong>. The AI responds using the currently selected model (Sonnet by default).
      </p>
      <p className="mt-2 text-sm">
        You can switch models using the model selector next to the input.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">3. Enable a tool</h2>
      <p className="mt-2 text-sm">
        Click the <strong>+</strong> or tool icon in the chat input to add Web Search or other tools to the current session. The AI will use them{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          automatically when relevant
        </Highlighter>
        .
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">4. Use an agent</h2>
      <p className="mt-2 text-sm">
        Click <strong>Agents</strong> in the sidebar to browse available agents. Selecting one starts a new chat pre-configured with that agent&apos;s prompt, model, and tools —{" "}
        <Highlighter action="highlight" color="#fde68a" darkColor="#92400e" isView animationDuration={800}>
          no setup required
        </Highlighter>
        .
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">5. Try a meeting</h2>
      <p className="mt-2 text-sm">
        Click <strong>Meetings</strong> in the sidebar → <strong>New Meeting</strong>. Upload a recording or start transcribing. When you&apos;re done,{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          generate minutes with one click
        </Highlighter>
        .
      </p>

      <hr className="my-6 border-border" />
      <p className="mt-2 text-sm">That&apos;s the core loop. From here, explore:</p>
      <ul className="list-inside list-disc mt-2 text-sm">
        <li className="py-1"><span className="font-medium text-primary underline cursor-pointer">Agents</span> — create your own AI configurations</li>
        <li className="py-1"><span className="font-medium text-primary underline cursor-pointer">Meetings &amp; Transcription</span> — the full meeting workflow</li>
        <li className="py-1"><span className="font-medium text-primary underline cursor-pointer">Memories</span> — make the AI remember your context</li>
      </ul>
    </div>
  );
}

function FirstChatContent() {
  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl">Your First Chat</h1>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Starting a conversation</h2>
      <p className="mt-2 text-sm">
        The chat input is at the bottom of the screen. Type anything and press <strong>Enter</strong> or click the send button.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Choosing a model</h2>
      <p className="mt-2 text-sm">Three capability tiers are available:</p>
      <div className="my-4 w-full overflow-hidden rounded-xl border border-border">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">Tier</th>
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">Best for</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr><td className="px-4 py-2 text-sm font-semibold">Standard</td><td className="px-4 py-2 text-sm">Everyday tasks — fast, capable, balanced</td></tr>
            <tr><td className="px-4 py-2 text-sm font-semibold">Advanced</td><td className="px-4 py-2 text-sm">Complex reasoning, long documents, hard problems</td></tr>
            <tr><td className="px-4 py-2 text-sm font-semibold">Lightweight</td><td className="px-4 py-2 text-sm">Quick answers, simple lookups</td></tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-sm">
        Switch models using the selector next to the chat input.{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          The choice persists until you change it again.
        </Highlighter>
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Temporary chat</h2>
      <p className="mt-2 text-sm">
        Click the dashed-message icon (top right) to enter <strong>Incognito mode</strong>.{" "}
        <Highlighter action="highlight" color="#fde68a" darkColor="#92400e" isView animationDuration={800}>
          The conversation isn&apos;t saved and memory is disabled.
        </Highlighter>{" "}
        Useful for sensitive or exploratory questions.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Attaching files</h2>
      <p className="mt-2 text-sm">
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          Drag a file onto the chat
        </Highlighter>{" "}
        or use the attachment button. Supported: PDF, DOCX, XLSX, PPTX, images, and plain text. The Code Execution tool must be enabled to process office files.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Chat history</h2>
      <p className="mt-2 text-sm">
        Previous chats appear in the sidebar under your team. Click <strong>Search</strong> to find a specific conversation by keyword.
      </p>
    </div>
  );
}

function ProjectsContent({ onNavigate }: { onNavigate?: (id: string) => void }) {
  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl flex items-center gap-2.5">
        <GalleryVerticalEndIcon size={24} className="shrink-0 text-foreground" />
        Projects
      </h1>
      <p className="mt-2 text-sm">
        A project is a focused workspace where you bring together the people, files, meetings, and conversations for a specific piece of work. Every chat you start inside a project runs with its files and instructions already applied —{" "}
        <Highlighter action="highlight" color="#fde68a" darkColor="#92400e" isView animationDuration={800}>
          no setup per conversation
        </Highlighter>
        .
      </p>

      <div className="mt-4 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
        <p className="font-medium mb-2">Regular chat vs. Project chat</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Regular chat</span>
            <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
              <li>🔲 No shared context</li>
              <li>🔲 Attach files each time</li>
              <li>🔲 Only you can see it</li>
              <li>🔲 No shared history</li>
            </ul>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Project chat</span>
            <ul className="flex flex-col gap-1 text-xs text-foreground">
              <li>✅ Instructions injected automatically</li>
              <li>✅ Project files always in context</li>
              <li>✅ Share with your team when ready</li>
              <li>✅ Memory builds up over time</li>
            </ul>
          </div>
        </div>
      </div>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Creating a project</h2>
      <ol className="list-inside list-decimal mt-2 text-sm">
        <li className="py-1">Click <strong>Projects</strong> in the sidebar</li>
        <li className="py-1">Click <strong>New Project</strong></li>
        <li className="py-1">Enter a name and optional description</li>
        <li className="py-1">Click <strong>Create project</strong></li>
      </ol>
      <p className="mt-2 text-sm text-muted-foreground">Projects are private by default — only invited members can see them.</p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">The project workspace</h2>
      <p className="mt-2 text-sm">
        The project page is split into two areas. On the left, a chat input and your conversations — project chats visible to all members, and private chats only visible to you. On the right, the{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          context panel
        </Highlighter>
        {" "}— instructions, attached files, meeting recordings, and memory.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Learn more</h2>
      <ul className="mt-2 text-sm flex flex-col gap-1">
        <li className="py-1">
          <button type="button" onClick={() => onNavigate?.("project-context")} className="font-medium text-primary underline cursor-pointer">
            Instructions &amp; Files
          </button>
          {" "}— set standing context, attach files and meeting recordings
        </li>
        <li className="py-1">
          <button type="button" onClick={() => onNavigate?.("project-members")} className="font-medium text-primary underline cursor-pointer">
            Members &amp; Sharing
          </button>
          {" "}— invite people, manage roles, share chats with the team
        </li>
        <li className="py-1">
          <button type="button" onClick={() => onNavigate?.("memories")} className="font-medium text-primary underline cursor-pointer">
            Memories
          </button>
          {" "}— how the AI remembers context across project conversations
        </li>
      </ul>

      <ProjectCreateTryIt />
    </div>
  );
}

function ProjectCreateTryIt() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Try it
        </p>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border border-border bg-muted/30 px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-muted-foreground" htmlFor="tryit-project-name">
            What are you working on?
          </label>
          <Input
            id="tryit-project-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name your project"
            className="bg-background"
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) {
                router.push("/projects/new");
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-muted-foreground" htmlFor="tryit-project-description">
            What are you trying to achieve? <span className="italic">(optional)</span>
          </label>
          <Textarea
            id="tryit-project-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your project, goals, subject, etc…"
            rows={3}
            className="resize-none bg-background"
          />
        </div>

        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <LockIcon size={11} />
            Private — only invited members can view
          </span>
          <Button
            type="button"
            size="sm"
            onClick={() => router.push("/projects/new")}
          >
            Create project
          </Button>
        </div>
      </div>
    </div>
  );
}

const DEMO_PROJECT_AGENTS = [
  { id: "a1", name: "Bridge Design Advisor", description: "NZS 3101 concrete design" },
  { id: "a2", name: "Stormwater Reviewer", description: "Rational Method & pipe sizing" },
];

const DEMO_PROJECT_WIDGETS = [
  { id: "f1", name: "Cover Compliance Checker", description: "Cover vs exposure class" },
  { id: "f2", name: "Stormwater Pipe Sizer", description: "Manning's capacity check" },
];

function ProjectAgentsWidgetsContent({
  content,
  pageIcon,
  pageTitle,
}: {
  content: string;
  pageIcon?: React.ElementType;
  pageTitle?: string;
}) {
  return (
    <div>
      <DocContent content={content} pageIcon={pageIcon} pageTitle={pageTitle} />
      <ProjectResourcesDemo />
    </div>
  );
}

function DemoResourceBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
      <UsersIcon size={10} /> All project users
    </span>
  );
}

function ProjectResourcesDemo() {
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [openedWidgetId, setOpenedWidgetId] = useState<string | null>(null);
  const selectedAgent = DEMO_PROJECT_AGENTS.find((a) => a.id === selectedAgentId) ?? null;
  const openedWidget = DEMO_PROJECT_WIDGETS.find((a) => a.id === openedWidgetId) ?? null;

  return (
    <div className="mt-8">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Try it
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Agents panel mock */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">Agents</span>
              <DemoResourceBadge />
            </div>
            <span className="flex size-6 items-center justify-center rounded text-muted-foreground">
              <PlusIcon size={12} />
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DEMO_PROJECT_AGENTS.map((a) => {
              const isSelected = selectedAgentId === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedAgentId(isSelected ? null : a.id)}
                  className={cn(
                    "flex flex-col rounded-lg border p-2.5 text-left transition-colors hover:bg-accent",
                    isSelected ? "border-primary bg-primary/5" : "border-border bg-background"
                  )}
                >
                  <BotIcon
                    size={18}
                    className={cn("mb-1.5", isSelected ? "text-primary" : "text-muted-foreground")}
                  />
                  <span className="truncate text-xs font-medium text-foreground">{a.name}</span>
                  <span className="mt-1 truncate text-[10px] text-muted-foreground">{a.description}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            {selectedAgent
              ? `Your next project chat will start as "${selectedAgent.name}". Click again to clear.`
              : "Click an agent to use it for your next project chat."}
          </p>
        </div>

        {/* Widgets panel mock */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">Widgets</span>
              <DemoResourceBadge />
            </div>
            <span className="flex size-6 items-center justify-center rounded text-muted-foreground">
              <PlusIcon size={12} />
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DEMO_PROJECT_WIDGETS.map((a) => {
              const isOpened = openedWidgetId === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setOpenedWidgetId(isOpened ? null : a.id)}
                  className={cn(
                    "flex flex-col rounded-lg border p-2.5 text-left transition-colors hover:bg-accent",
                    isOpened ? "border-primary bg-primary/5" : "border-border bg-background"
                  )}
                >
                  <LayoutPanelTopIcon
                    size={18}
                    className={cn("mb-1.5", isOpened ? "text-primary" : "text-muted-foreground")}
                  />
                  <span className="truncate text-xs font-medium text-foreground">{a.name}</span>
                  <span className="mt-1 truncate text-[10px] text-muted-foreground">{a.description}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            {openedWidget
              ? `"${openedWidget.name}" opens on the widget page — viewable by everyone on the project.`
              : "Attached widgets are viewable by everyone. Click a card to open it."}
          </p>
        </div>
      </div>
    </div>
  );
}

function AgentsContent({ onNavigate }: { onNavigate?: (id: string) => void }) {
  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl flex items-center gap-2.5">
        <BotMessageSquareIcon size={24} className="shrink-0 text-foreground" />
        Agents
      </h1>
      <p className="mt-2 text-sm">
        Imagine asking a new colleague the same background questions every single morning — your role, your project, the way you like things written. An agent is how you stop doing that.{" "}
        <Highlighter action="highlight" color="#fde68a" darkColor="#92400e" isView animationDuration={800}>
          You configure it once, and every conversation starts with the AI already briefed.
        </Highlighter>
      </p>

      <div className="mt-4 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
        <p className="font-medium mb-2">General chat vs. Agent</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">General chat</span>
            <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
              <li>🔲 Blank slate every time</li>
              <li>🔲 You explain your context each session</li>
              <li>🔲 You pick tools manually</li>
              <li>🔲 Responses vary with no fixed persona</li>
            </ul>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Agent</span>
            <ul className="flex flex-col gap-1 text-xs text-foreground">
              <li>✅ Pre-briefed with your instructions</li>
              <li>✅ Context is baked in from the start</li>
              <li>✅ Tools are pre-configured</li>
              <li>✅ Consistent behaviour every time</li>
            </ul>
          </div>
        </div>
      </div>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Creating an agent</h2>
      <ol className="list-inside list-decimal mt-2 text-sm">
        <li className="py-1">Click <strong>Agents</strong> in the sidebar</li>
        <li className="py-1">Click <strong>Create new agent</strong></li>
        <li className="py-1">Fill in the details (see below)</li>
        <li className="py-1">Click <strong>Save</strong></li>
      </ol>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Agent settings</h2>

      <h3 className="mt-6 mb-2 font-semibold text-xl">Basic</h3>
      <ul className="list-inside list-disc mt-2 text-sm">
        <li className="py-1"><strong>Name</strong> — displayed in chats and the agent list</li>
        <li className="py-1"><strong>Type</strong> — <em>Knowledge</em> (focused on a domain) or <em>Custom</em> (general purpose)</li>
        <li className="py-1"><strong>Description</strong> — a short summary of what the agent does</li>
        <li className="py-1"><strong>Model</strong> — the default model for chats using this agent</li>
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-xl">Instructions</h3>
      <p className="mt-2 text-sm">
        The instructions define the agent&apos;s persona, expertise, and behaviour. They&apos;re{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          injected at the start of every conversation
        </Highlighter>
        . Use them to:
      </p>
      <ul className="list-inside list-disc mt-2 text-sm">
        <li className="py-1">Establish role and tone (&ldquo;You are a senior civil engineer…&rdquo;)</li>
        <li className="py-1">Set output format expectations</li>
        <li className="py-1">Provide standing context (project names, terminology)</li>
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-xl">Tools</h3>
      <p className="mt-2 text-sm">Toggle which tools the agent can use by default:</p>
      <ul className="list-inside list-disc mt-2 text-sm">
        <li className="py-1">Web search</li>
        <li className="py-1">Web fetch</li>
        <li className="py-1">Code execution</li>
        <li className="py-1">Memory</li>
        <li className="py-1">
          <button
            type="button"
            onClick={() => onNavigate?.("knowledge-base")}
            className="font-medium text-primary underline cursor-pointer"
          >
            Library
          </button>
          {" "}— attach files and collections so the agent can reference them in every chat
        </li>
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-xl">Context</h3>
      <p className="mt-2 text-sm">Control what background context is included automatically:</p>
      <ul className="list-inside list-disc mt-2 text-sm">
        <li className="py-1"><strong>Your profile</strong> — includes your name, role, and bio</li>
        <li className="py-1"><strong>Team context</strong> — includes team-level instructions</li>
        <li className="py-1"><strong>Woods context</strong> — Woods-specific background</li>
        <li className="py-1"><strong>NZ context</strong> — New Zealand regulatory and industry context</li>
        <li className="py-1"><strong>Civil engineering context</strong> — domain knowledge for engineering work</li>
      </ul>

      <h3 className="mt-6 mb-2 font-semibold text-xl">Team assignment</h3>
      <p className="mt-2 text-sm">
        Assign the agent to one or more teams so the right team members can access it.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Using an agent</h2>
      <p className="mt-2 text-sm">
        Click <strong>Agents</strong> in the sidebar, select an agent, and click <strong>Chat</strong>. A new conversation opens pre-loaded with the agent&apos;s configuration.
      </p>
      <p className="mt-2 text-sm">
        You can override the model or toggle tools within a specific conversation{" "}
        <Highlighter action="underline" color="#64748b" isView animationDuration={600}>
          without affecting the agent&apos;s default settings
        </Highlighter>
        .
      </p>

      <AgentChatSnippet />
    </div>
  );
}

function AgentChatSnippet() {
  const initialMessages = useMemo(() => toUIMessages(AGENT_DEMO_SEED), []);
  const { messages, setMessages, sendMessage, status, stop } =
    useChat<ChatUIMessage>({ transport: AGENT_DEMO_TRANSPORT });
  const [input, setInput] = useState("");
  const isStreaming = status === "streaming" || status === "submitted";
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    setMessages(initialMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newLength = messages.length;
    const shouldScroll = newLength > prevLengthRef.current && prevLengthRef.current > 0;
    prevLengthRef.current = newLength;
    if (shouldScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Try it
        </p>
        <span className="text-[10px] text-muted-foreground">Haiku · not saved</span>
      </div>

      <div className="flex flex-col rounded-xl border border-border overflow-hidden">
        {/* Agent header */}
        <div className="flex flex-col gap-3 border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <BotIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="rounded-md border border-border bg-background px-2.5 py-1 text-sm font-medium">
              Civil Engineering Advisor
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground">Tools</span>
            <div className="flex flex-col gap-3 rounded-md border border-border bg-background px-3 py-2">
              <div className="flex flex-col gap-2">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary self-start">
                  Web Search
                </span>
                <div className="flex flex-col gap-1 pl-1">
                  <span className="text-[10px] text-muted-foreground">Allowed domains</span>
                  <textarea
                    readOnly
                    rows={2}
                    className="w-full resize-none rounded border border-border bg-muted/50 px-2 py-1.5 text-xs text-foreground outline-none"
                    value={"https://www.building.govt.nz/\nhttps://www.lbp.govt.nz/"}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary self-start">
                  Knowledge Base
                </span>
                <div className="flex flex-col gap-1 pl-1">
                  <span className="text-[10px] text-muted-foreground">Files</span>
                  <div className="flex flex-wrap gap-1.5">
                    {["NZS 3604 Summary.pdf", "Site Investigation Report.docx"].map((f) => (
                      <span key={f} className="rounded border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-foreground">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground">Instructions</span>
            <textarea
              readOnly
              rows={3}
              className="w-full resize-none rounded-md border border-border bg-background px-2.5 py-2 text-xs text-foreground outline-none"
              value="You are a senior civil engineer at a New Zealand consultancy. Give concise, practical advice. Reference NZ standards (NZS 3604, AS/NZS standards) where relevant. Keep responses focused and under 200 words."
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex flex-col gap-4 px-5 py-4 max-h-96 overflow-y-auto bg-muted/30">
          {messages.map((msg) => {
            const text = msg.parts
              .filter((p) => p.type === "text")
              .map((p) => (p as { type: "text"; text: string }).text)
              .join("");
            if (!text) return null;
            const isUser = msg.role === "user";
            return (
              <div
                key={msg.id}
                className={cn("flex gap-3", isUser ? "justify-end" : "justify-start")}
              >
                {!isUser ? (
                  <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground text-[10px] font-semibold text-background">
                    W
                  </div>
                ) : null}
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                    isUser
                      ? "bg-secondary text-foreground rounded-br-sm"
                      : "bg-background border border-border text-foreground rounded-bl-sm"
                  )}
                >
                  <MessageResponse
                    isAnimating={isStreaming && msg === messages[messages.length - 1]}
                    disallowedElements={["script", "style"]}
                  >
                    {text}
                  </MessageResponse>
                </div>
                {isUser ? (
                  <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                    You
                  </div>
                ) : null}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="flex items-end gap-2 border-t border-border bg-background px-3 py-2">
          <textarea
            className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none py-1.5 min-h-9 max-h-24"
            placeholder="Ask the Civil Engineering Advisor…"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
          />
          {isStreaming ? (
            <Button
              size="icon"
              variant="ghost"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={stop}
              type="button"
            >
              <SquareIcon className="size-3.5" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
              onClick={handleSend}
              disabled={!input.trim()}
              type="button"
            >
              <SendIcon className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const DEMO_ATTENDEES = ["Sarah K.", "James M.", "Priya R."];
const DEMO_NOTES = `# Project Kick-off — Meeting Notes

**Date:** ${new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" })}

## Attendees

| Name | Role |
|------|------|
| Sarah K. | Project Lead |
| James M. | Site Engineer |
| Priya R. | Finance |

## Summary

Introductory meeting to align on project scope and delivery milestones for the new commercial build. Option B was confirmed as the preferred foundation design.

## Discussion points

- Site survey timeline reviewed — James to complete by 9 May
- Budget allocation discussed — awaiting sign-off from Priya
- Foundation type: bored piers below active clay zone recommended

## Action items

| # | Owner | Action | Due |
|---|-------|--------|-----|
| 1 | James M. | Share site survey report | 9 May |
| 2 | Priya R. | Confirm budget allocation | 12 May |
| 3 | Sarah K. | Circulate minutes | 6 May |

## Decisions

- Proceed with **Option B** for foundation design (bored pier)
- Next meeting scheduled for **19 May**
`;

function KnowledgeBaseContent({ onNavigate }: { onNavigate?: (id: string) => void }) {
  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl flex items-center gap-2.5">
        <FoldersIcon size={24} className="shrink-0 text-foreground" />
        Library
      </h1>
      <p className="mt-2 text-sm">
        The Library is a file library for your team. Upload documents once and attach them to agents — so the AI can reference your files in every conversation{" "}
        <Highlighter action="highlight" color="#fde68a" darkColor="#92400e" isView animationDuration={800}>
          without you needing to re-attach them each time.
        </Highlighter>
      </p>
      <div className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <strong className="text-foreground">Files only.</strong> The Library stores documents. Chat history and conversations are not stored here.
      </div>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">How files are organised</h2>
      <p className="mt-2 text-sm">Files sit in a three-level hierarchy:</p>
      <div className="my-3 w-full overflow-hidden rounded-xl border border-border">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">Level</th>
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">What it is</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr><td className="px-4 py-2 text-sm font-semibold">Team</td><td className="px-4 py-2 text-sm">A top-level container for a team&apos;s files</td></tr>
            <tr><td className="px-4 py-2 text-sm font-semibold">Collection</td><td className="px-4 py-2 text-sm">A folder inside a team for grouping related documents</td></tr>
            <tr><td className="px-4 py-2 text-sm font-semibold">File</td><td className="px-4 py-2 text-sm">An individual document</td></tr>
          </tbody>
        </table>
      </div>

      <p className="mt-2 mb-2 text-sm">Here&apos;s what it looks like — click to expand:</p>
      <FileTree
        className="text-sm"
        defaultExpanded={new Set(["ws-1", "ws-1/col-1"])}
      >
        <FileTreeFolder path="ws-1" name="Civil Engineering Team">
          <FileTreeFolder path="ws-1/col-1" name="NZ Standards">
            <FileTreeFile path="ws-1/col-1/f1" name="NZS 3604 - Timber Framed Buildings.pdf" />
            <FileTreeFile path="ws-1/col-1/f2" name="NZBC Clause B1 Structure.pdf" />
            <FileTreeFile path="ws-1/col-1/f3" name="AS/NZS 1170 Loading Standards.pdf" />
          </FileTreeFolder>
          <FileTreeFolder path="ws-1/col-2" name="Site Reports">
            <FileTreeFile path="ws-1/col-2/f1" name="Site Investigation - Lot 42.docx" />
            <FileTreeFile path="ws-1/col-2/f2" name="Geotechnical Summary.pdf" />
          </FileTreeFolder>
        </FileTreeFolder>
      </FileTree>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Uploading files</h2>
      <ol className="list-inside list-decimal mt-2 text-sm">
        <li className="py-1">Click <strong>Library</strong> in the sidebar</li>
        <li className="py-1">Select the team and collection you want to upload into</li>
        <li className="py-1">Click <strong>Upload</strong> or drag and drop files onto the panel</li>
      </ol>
      <p className="mt-2 text-sm text-muted-foreground">Supported: PDF, DOCX, XLSX, PPTX, images, and plain text.</p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Using files in your agents</h2>
      <p className="mt-2 text-sm">
        Files can be attached to any{" "}
        <button type="button" onClick={() => onNavigate?.("agents")} className="font-medium text-primary underline cursor-pointer">agent</button>
        {" "}you own. When a chat starts with that agent, the relevant content is retrieved automatically.
      </p>
      <ol className="list-inside list-decimal mt-2 text-sm">
        <li className="py-1">Click <strong>Agents</strong> in the sidebar and open the agent editor</li>
        <li className="py-1">Go to <strong>Tools</strong> and enable <strong>Knowledge Base</strong></li>
        <li className="py-1">Select the files or collections the agent should reference</li>
      </ol>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Requesting to move files</h2>
      <p className="mt-2 text-sm">
        If a file belongs in a different team, you can request a transfer rather than re-uploading it.
      </p>
      <ol className="list-inside list-decimal mt-2 text-sm">
        <li className="py-1">Select the file in the Library panel</li>
        <li className="py-1">Click <strong>Request move</strong> and choose the target team</li>
        <li className="py-1">The team owner approves or declines the request</li>
      </ol>
      <p className="mt-2 text-sm text-muted-foreground">
        Admins can view all pending requests under <strong>File Move Requests</strong> in the menu.
      </p>
    </div>
  );
}

function MeetingsContent() {
  const [recording, setRecording] = useState<"idle" | "recording" | "muted">("idle");
  const [notesGenerated, setNotesGenerated] = useState(false);
  const [_attendees, setAttendees] = useState<string[]>([]);
  const [notes, setNotes] = useState(DEMO_NOTES);
  const [editing, setEditing] = useState(false);
  const [layout, setLayout] = useState("general");
  const [manualAttendees, setManualAttendees] = useState<string[]>(["@sarah", "@james"]);
  const [attendeeInput, setAttendeeInput] = useState("");

  const handleStop = () => {
    setRecording("idle");
    setAttendees(DEMO_ATTENDEES);
    // Auto-add detected attendees as @chips if not already present
    setManualAttendees(prev => {
      const existing = new Set(prev.map(a => a.toLowerCase()));
      const auto = DEMO_ATTENDEES.map(a => `@${a.split(" ")[0].toLowerCase()}`).filter(a => !existing.has(a));
      return [...prev, ...auto];
    });
    setNotesGenerated(true);
  };

  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl flex items-center gap-2.5">
        <AudioLinesIcon size={24} className="shrink-0 text-foreground" />
        Meetings &amp; Transcription
      </h1>
      <p className="mt-2 text-sm">
        The Meetings feature handles the full lifecycle of a meeting: recording → transcription → minutes → distribution.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">How it works</h2>

      {/* ── Wireframe ── */}
      <div className="mt-4 rounded-xl border border-border overflow-hidden">
        {/* Window chrome */}
        <div className="flex items-center gap-1.5 bg-muted/60 border-b border-border px-4 py-2.5">
          <span className="size-2.5 rounded-full bg-destructive/50" />
          <span className="size-2.5 rounded-full bg-amber-400/50" />
          <span className="size-2.5 rounded-full bg-green-400/50" />
          <span className="ml-3 text-[11px] text-muted-foreground font-medium">Project Kick-off — Woods AI</span>
        </div>

        {/* Page body */}
        <div className="bg-background px-6 py-5 flex flex-col gap-5">

          {/* Meeting header row */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-muted-foreground mb-0.5">Meeting title</p>
              <p className="text-sm font-semibold text-foreground">Project Kick-off</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground mb-0.5">Layout</p>
              <select
                value={layout}
                onChange={(e) => setLayout(e.target.value)}
                className="rounded-md border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground outline-none cursor-pointer hover:bg-muted transition-colors"
              >
                <option value="general">General</option>
                <option value="engineering">Engineering Site Meeting</option>
                <option value="survey">Survey Team Meeting</option>
              </select>
            </div>
          </div>

          {/* Attendees field */}
          <div>
            <p className="text-[11px] text-muted-foreground mb-1">Attendees</p>
            <div
              className="flex flex-wrap gap-1.5 rounded-md border border-border bg-muted/30 px-2.5 py-2 min-h-9 cursor-text"
              onClick={() => document.getElementById("attendee-input")?.focus()}
            >
              {manualAttendees.map((a) => (
                <span
                  key={a}
                  className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary"
                >
                  {a}
                  <button
                    type="button"
                    className="ml-0.5 text-primary/60 hover:text-primary leading-none"
                    onClick={(e) => { e.stopPropagation(); setManualAttendees(prev => prev.filter(x => x !== a)); }}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                id="attendee-input"
                type="text"
                value={attendeeInput}
                onChange={(e) => setAttendeeInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === "," || e.key === " ") && attendeeInput.trim()) {
                    e.preventDefault();
                    const val = attendeeInput.trim().replace(/,$/, "");
                    if (val && !manualAttendees.includes(val)) {
                      setManualAttendees(prev => [...prev, val.startsWith("@") ? val : `@${val}`]);
                    }
                    setAttendeeInput("");
                  } else if (e.key === "Backspace" && !attendeeInput && manualAttendees.length) {
                    setManualAttendees(prev => prev.slice(0, -1));
                  }
                }}
                placeholder={manualAttendees.length ? "" : "@name…"}
                className="flex-1 min-w-20 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none py-0.5"
              />
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">Type a name and press Enter or Space. Auto-populated from the transcript after recording.</p>
          </div>

          {/* Context field */}
          <div>
            <p className="text-[11px] text-muted-foreground mb-1">Meeting context <span className="italic">(optional)</span></p>
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Foundation design review for new commercial build, Option B preferred…
            </div>
          </div>

          {/* Recording controls */}
          <div className="relative">
            <p className="text-[11px] text-muted-foreground mb-2">Recording controls</p>

            {/* Live transcript area */}
            <div className={cn(
              "flex items-center justify-center rounded-lg border border-border bg-muted/30 transition-all",
              recording !== "idle" ? "h-16" : "h-10"
            )}>
              {recording === "idle" && !notesGenerated && (
                <p className="text-[11px] text-muted-foreground">Press <strong>Start</strong> to begin recording.</p>
              )}
              {recording !== "idle" && (
                <div className="flex items-center gap-2">
                  <span className="size-2 rounded-full bg-destructive animate-pulse" />
                  <span className="text-xs text-muted-foreground">
                    {recording === "muted" ? "Microphone muted — recording continues…" : "Recording & transcribing…"}
                  </span>
                </div>
              )}
              {notesGenerated && recording === "idle" && (
                <p className="text-[11px] text-muted-foreground">Recording stopped. Notes generated below.</p>
              )}
            </div>

            {/* Floating pill toolbar — mirrors the real meeting minutes panel */}
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              {/* Left pill: record controls + autosave */}
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/95 p-2 shadow-lg backdrop-blur">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label={!recording || recording === "idle" ? "Start" : recording === "muted" ? "Unmute" : "Mute"}
                      className="size-9 rounded-full border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15"
                      onClick={() => setRecording(r => r === "idle" ? "recording" : r === "recording" ? "muted" : "recording")}
                      size="icon"
                      type="button"
                      variant="outline"
                    >
                      {recording === "idle" ? (
                        <PlayIcon className="text-current" size={16} />
                      ) : recording === "muted" ? (
                        <MicOffIcon className="size-4" />
                      ) : (
                        <MicIcon className="size-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{recording === "idle" ? "Start" : recording === "muted" ? "Unmute" : "Mute"}</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="Stop"
                      className="size-9 rounded-full border-border bg-muted text-muted-foreground hover:bg-muted/90"
                      disabled={recording === "idle"}
                      onClick={handleStop}
                      size="icon"
                      type="button"
                      variant="outline"
                    >
                      <Square className="text-current" size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Stop &amp; Generate Minutes</p></TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={cn(
                      "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm",
                      notesGenerated
                        ? "border-green-500/40 bg-muted text-green-600 dark:text-green-400"
                        : "bg-background text-muted-foreground"
                    )}>
                      <CheckCircle2Icon className={cn("size-3.5", notesGenerated && "animate-in zoom-in-95 duration-200")} />
                      <span>{notesGenerated ? "Saved" : "Auto-save"}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Left-column fields are saved automatically.</p></TooltipContent>
                </Tooltip>
              </div>

              {/* Right pill: download / copy */}
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/95 p-2 shadow-lg backdrop-blur">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="Download"
                      className="rounded-full"
                      size="icon"
                      type="button"
                      variant="outline"
                      disabled={!notesGenerated}
                    >
                      <DownloadIcon className="text-current" size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Download</p></TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="Copy"
                      className="rounded-full"
                      onClick={() => notesGenerated && navigator.clipboard.writeText(notes)}
                      size="icon"
                      type="button"
                      variant="outline"
                      disabled={!notesGenerated}
                    >
                      <CopyIcon className="text-current" size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Copy</p></TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Generated notes */}
          {notesGenerated && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">Meeting notes <span className="text-green-600 dark:text-green-400">(generated)</span></p>
                <button
                  type="button"
                  onClick={() => setEditing(e => !e)}
                  className="text-[11px] text-primary underline"
                >
                  {editing ? "Preview" : "Edit"}
                </button>
              </div>
              {editing ? (
                <textarea
                  className="w-full resize-none rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-mono leading-relaxed"
                  rows={14}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  autoFocus
                />
              ) : (
                <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm">
                  <MessageResponse
                    isAnimating={false}
                  >
                    {notes}
                  </MessageResponse>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">Use the toolbar above to copy or download. Click <strong>Edit</strong> to make changes.</p>
            </div>
          )}
        </div>
      </div>
      {/* ── End wireframe ── */}

      <div className="mt-1 mb-2 text-[11px] text-muted-foreground text-center">Interactive preview — click the controls above to try the flow</div>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Recording controls explained</h2>
      <div className="my-4 w-full overflow-hidden rounded-xl border border-border">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50">
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">Control</th>
              <th className="px-4 py-2 text-sm text-left font-semibold border-b border-border">What it does</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <tr><td className="px-4 py-2 text-sm"><span className="inline-flex items-center gap-1.5"><PlayIcon size={14} className="text-current" /> Record</span></td><td className="px-4 py-2 text-sm">Starts the session and begins live transcription</td></tr>
            <tr><td className="px-4 py-2 text-sm"><span className="inline-flex items-center gap-1.5"><MicOffIcon className="size-3.5" /> Mute / <MicIcon className="size-3.5" /> Unmute</span></td><td className="px-4 py-2 text-sm">Toggles your microphone while recording continues</td></tr>
            <tr><td className="px-4 py-2 text-sm"><span className="inline-flex items-center gap-1.5"><Square className="size-3.5" /> Stop</span></td><td className="px-4 py-2 text-sm">Ends the session and triggers automatic note generation</td></tr>
            <tr><td className="px-4 py-2 text-sm">Edit</td><td className="px-4 py-2 text-sm">Click into the notes area to make inline changes</td></tr>
            <tr><td className="px-4 py-2 text-sm"><span className="inline-flex items-center gap-1.5"><CopyIcon size={14} className="text-current" /> Copy</span></td><td className="px-4 py-2 text-sm">Copies the formatted notes to your clipboard</td></tr>
            <tr><td className="px-4 py-2 text-sm"><span className="inline-flex items-center gap-1.5"><DownloadIcon size={14} className="text-current" /> Download</span></td><td className="px-4 py-2 text-sm">Downloads a PDF formatted in the Woods template</td></tr>
          </tbody>
        </table>
      </div>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Meeting context</h2>
      <p className="mt-2 text-sm">
        Before generating notes, add context in the <strong>Meeting context</strong> field — what the meeting was about, key decisions, acronyms, or project names. This significantly improves the quality of the output.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Layouts</h2>
      <p className="mt-2 text-sm">
        Notes follow a <strong>layout template</strong>. Select a layout from the dropdown on the meeting page. You can create your own layouts from <strong>Meetings → Create new layout</strong> and share them with your team.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Exporting</h2>
      <p className="mt-2 text-sm">
        Use <strong>Download</strong> to save a PDF in the Woods template, or <strong>Copy</strong> to paste the notes elsewhere. You can also email the minutes directly from the meeting page.
      </p>
    </div>
  );
}

// ── Prompt Library ────────────────────────────────────────────────────────────

const DEMO_PROMPTS = [
  {
    id: "p1",
    name: "Summarise this document",
    content: "Please summarise the key points from the following document in bullet points, highlighting any action items or decisions:",
  },
  {
    id: "p2",
    name: "Draft an email",
    content: "Draft a professional email to the client confirming the meeting details, agenda, and next steps discussed today.",
  },
  {
    id: "p3",
    name: "Risk assessment",
    content: "Identify and list the top 5 risks for this project, including likelihood, impact, and a recommended mitigation strategy for each.",
  },
  {
    id: "p4",
    name: "Weekly status update",
    content: "Write a concise weekly status update covering: what was completed this week, what's in progress, any blockers, and the plan for next week.",
  },
  {
    id: "p5",
    name: "Meeting agenda",
    content: "Create a structured meeting agenda for a 45-minute project review, including time slots for each agenda item.",
  },
  {
    id: "p6",
    name: "Translate to plain English",
    content: "Rewrite the following technical content in plain English so it can be understood by a non-technical stakeholder:",
  },
];

function PromptLibraryInner() {
  const controller = usePromptInputController();
  const [lastSent, setLastSent] = useState<string | null>(null);
  const [dragOverInput, setDragOverInput] = useState(false);
  const dragCounter = useRef(0);

  const insertPrompt = (content: string) => {
    const current = controller.textInput.value.trim();
    controller.textInput.setInput(current ? `${current}\n\n${content}` : content);
  };

  const handleSubmit = (msg: PromptInputMessage) => {
    const text = msg.text?.trim();
    if (!text) return;
    setLastSent(text);
    controller.textInput.clear();
  };

  return (
    <div>
      <h1 className="mt-6 mb-2 font-semibold text-3xl flex items-center gap-2.5">
        <MessageSquareDashedIcon size={24} className="shrink-0 text-foreground" />
        Prompt Library
      </h1>
      <p className="mt-2 text-sm">
        Save prompts you use repeatedly and insert them into any conversation with one click — or drag them straight into the chat input.
      </p>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Try it</h2>
      <p className="mt-1 mb-4 text-sm text-muted-foreground">
        Click a prompt to insert it, or drag it into the input below.
      </p>

      {/* Prompt cards */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {DEMO_PROMPTS.map((p) => (
          <div
            key={p.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData("text/plain", p.content);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => insertPrompt(p.content)}
            className="group flex cursor-pointer items-start gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2.5 transition-colors hover:bg-muted/60 active:scale-[0.99]"
          >
            <GripVerticalIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground">{p.name}</p>
              <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{p.content.slice(0, 72)}…</p>
            </div>
          </div>
        ))}
      </div>

      {/* Drop zone + real PromptInput */}
      <div
        className={cn(
          "relative mt-4 overflow-hidden rounded-2xl border transition-colors",
          dragOverInput ? "border-primary bg-primary/5" : "border-border bg-background shadow-lg"
        )}
        onDragEnter={(e) => { e.preventDefault(); dragCounter.current++; setDragOverInput(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => { dragCounter.current--; if (dragCounter.current === 0) setDragOverInput(false); }}
        onDrop={(e) => {
          e.preventDefault();
          dragCounter.current = 0;
          setDragOverInput(false);
          const text = e.dataTransfer.getData("text/plain");
          if (text) insertPrompt(text);
        }}
      >
        {dragOverInput && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <p className="rounded-lg bg-primary/90 px-3 py-1.5 text-xs font-medium text-primary-foreground">Drop to insert prompt</p>
          </div>
        )}
        <PromptInput onSubmit={handleSubmit}>
          <PromptInputBody>
            <PromptInputTextarea placeholder="Click a prompt above, or type your message…" />
          </PromptInputBody>
          <PromptInputFooter>
            <PromptInputTools />
            <PromptInputTools>
              <PromptInputSubmit />
            </PromptInputTools>
          </PromptInputFooter>
        </PromptInput>
      </div>

      {lastSent && (
        <div className="mt-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
          <p className="text-[11px] text-muted-foreground mb-1">Sent to AI:</p>
          <p className="text-sm text-foreground">{lastSent}</p>
        </div>
      )}

      <h2 className="mt-8 mb-2 font-semibold text-2xl">Creating a prompt</h2>
      <ol className="list-inside list-decimal mt-2 text-sm space-y-1">
        <li>Click <strong>Prompts</strong> in the sidebar</li>
        <li>Click <strong>Create new prompt</strong></li>
        <li>Give it a name and write the prompt content</li>
        <li>Choose <strong>Private</strong> or <strong>Share with team</strong></li>
        <li>Save</li>
      </ol>

      <h2 className="mt-6 mb-2 font-semibold text-2xl">Using a saved prompt</h2>
      <p className="mt-2 text-sm">
        Open the Prompt Library from the sidebar, click a prompt, and it is inserted into the current chat input — ready to send or edit before sending.
      </p>
    </div>
  );
}

function PromptLibraryContent() {
  return (
    <PromptInputProvider>
      <PromptLibraryInner />
    </PromptInputProvider>
  );
}

function ChatSnippet({ seed }: { seed: DocSnippetMessage[] }) {
  const initialMessages = useMemo(() => toUIMessages(seed), [seed]);
  const { messages, setMessages, sendMessage, status, stop } =
    useChat<ChatUIMessage>({ transport: DOCS_TRANSPORT });
  const [input, setInput] = useState("");
  const isStreaming = status === "streaming" || status === "submitted";
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    setMessages(initialMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newLength = messages.length;
    // Only scroll when a real message is added after the seed is loaded —
    // not during the initial setMessages() population (prevLength would be 0).
    const shouldScroll = newLength > prevLengthRef.current && prevLengthRef.current > 0;
    prevLengthRef.current = newLength;
    if (shouldScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    sendMessage({ text });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Try it
        </p>
        <span className="text-[10px] text-muted-foreground">
          Haiku · no tools · not saved
        </span>
      </div>

      <div className="flex flex-col rounded-xl border border-border bg-muted/30 overflow-hidden">
        <div className="flex flex-col gap-4 px-5 py-4 max-h-96 overflow-y-auto">
          {messages.map((msg) => {
            const text = msg.parts
              .filter((p) => p.type === "text")
              .map((p) => (p as { type: "text"; text: string }).text)
              .join("");
            if (!text) return null;
            const isUser = msg.role === "user";
            return (
              <div
                key={msg.id}
                className={cn("flex gap-3", isUser ? "justify-end" : "justify-start")}
              >
                {!isUser ? (
                  <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground text-[10px] font-semibold text-background">
                    W
                  </div>
                ) : null}
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                    isUser
                      ? "bg-secondary text-foreground rounded-br-sm"
                      : "bg-background border border-border text-foreground rounded-bl-sm"
                  )}
                >
                  <MessageResponse
                    isAnimating={isStreaming && msg === messages[messages.length - 1]}
                    disallowedElements={["script", "style"]}
                  >
                    {text}
                  </MessageResponse>
                </div>
                {isUser ? (
                  <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                    You
                  </div>
                ) : null}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className="flex items-end gap-2 border-t border-border bg-background px-3 py-2">
          <textarea
            className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none py-1.5 min-h-9 max-h-24"
            placeholder="Ask a follow-up…"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
          />
          {isStreaming ? (
            <Button
              size="icon"
              variant="ghost"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={stop}
              type="button"
            >
              <SquareIcon className="size-3.5" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-30"
              onClick={handleSend}
              disabled={!input.trim()}
              type="button"
            >
              <SendIcon className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
